import { RotateCcw, Save, Send } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useAdminAuth } from '../auth/AdminAuthProvider'
import { hasMinRole } from '../auth/adminAuthorization'
import { discardCmsDraft, fetchCmsContent, fetchCmsVersions, formatDateTime, publishCmsContent, rollbackCmsContent, saveCmsDraft } from '../lib/cms'
import { validateHomeConfigDraft } from '../../../../packages/validation/cms'
import { EMPTY_HOME_CONFIG_DATA } from '../../../../packages/types/cms'
import HomeHeroPreview from '../components/HomeHeroPreview'
import HomeHeroStateSelector from '../components/HomeHeroStateSelector'
import ShuttleVisualQa from '../components/ShuttleVisualQa'
import { ADMIN_HOME_HERO_PREVIEW_MODES } from '../components/homeHeroPreviewModes'

const TODAY_CHIP_LABELS = { cafeteria: '학식', shuttle: '셔틀', sugiInstant: '수기즉답' }
const UTILITY_LABELS = { notice: '공지사항', credit: '학점관리', report: '불편 제보' }
const DISABLEABLE_LABELS = { ...TODAY_CHIP_LABELS, ...UTILITY_LABELS }
const TIME_PRESETS = ['now', '09:00', '12:00', '15:00', '18:00', '21:00']
const HERO_QA_STATE_ORDER = [...ADMIN_HOME_HERO_PREVIEW_MODES, { id: 'maintenance', label: '홈 시스템 점검' }]

function formatSimulatedDate(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const hh = String(date.getHours()).padStart(2, '0')
  const mm = String(date.getMinutes()).padStart(2, '0')
  return `${y}.${m}.${d} ${hh}:${mm}`
}

function toInputDateTimeValue(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const hh = String(date.getHours()).padStart(2, '0')
  const mm = String(date.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${d}T${hh}:${mm}`
}

function moveEntry(list, id, direction) {
  const idx = list.findIndex(e => e.id === id)
  const swapIdx = idx + direction
  if (idx < 0 || swapIdx < 0 || swapIdx >= list.length) return list
  const sorted = [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  const i = sorted.findIndex(e => e.id === id)
  const j = i + direction
  if (j < 0 || j >= sorted.length) return list
  const a = sorted[i], b = sorted[j]
  return list.map(entry => entry.id === a.id ? { ...entry, order: b.order } : entry.id === b.id ? { ...entry, order: a.order } : entry)
}

function CardOrderGroup({ group, labels, config, setConfig, canEdit }) {
  const items = [...config.cards[group]].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  return (
    <div>
      {items.map((entry, idx) => (
        <div key={entry.id} className="card-order-row">
          <label className="check-chip">
            <input type="checkbox" disabled={!canEdit} checked={entry.visible} onChange={e => setConfig(current => ({ ...current, cards: { ...current.cards, [group]: current.cards[group].map(item => item.id === entry.id ? { ...item, visible: e.target.checked } : item) } }))} />
            {labels[entry.id] || entry.id}
          </label>
          <button type="button" className="icon-button" style={{ width: 30, height: 30 }} aria-label={`${labels[entry.id] || entry.id} 위로`} disabled={!canEdit || idx === 0} onClick={() => setConfig(current => ({ ...current, cards: { ...current.cards, [group]: moveEntry(current.cards[group], entry.id, -1) } }))}>↑</button>
          <button type="button" className="icon-button" style={{ width: 30, height: 30 }} aria-label={`${labels[entry.id] || entry.id} 아래로`} disabled={!canEdit || idx === items.length - 1} onClick={() => setConfig(current => ({ ...current, cards: { ...current.cards, [group]: moveEntry(current.cards[group], entry.id, 1) } }))}>↓</button>
        </div>
      ))}
    </div>
  )
}

export default function HomeConfigPage() {
  const auth = useAdminAuth()
  const canEdit = hasMinRole(auth.role, 'editor')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [production, setProduction] = useState(null)
  const [draft, setDraft] = useState(null)
  const [config, setConfig] = useState(EMPTY_HOME_CONFIG_DATA)
  const [versions, setVersions] = useState([])
  const [previewMode, setPreviewMode] = useState(null) // null: 시간 기준 실시간 계산 (기본 모드 A)
  const [maintenancePreview, setMaintenancePreview] = useState(false)
  const [workspaceMode, setWorkspaceMode] = useState('preview')
  const [qaSubMode, setQaSubMode] = useState('all') // 'all' | 'shuttle'
  const [timePreset, setTimePreset] = useState('now') // 'now' | '09:00' | '12:00' | '15:00' | '18:00' | '21:00' | 'custom'
  const [customDate, setCustomDate] = useState(() => new Date())
  const statePanelRef = useRef(null)
  const savedSnapshotRef = useRef(JSON.stringify(EMPTY_HOME_CONFIG_DATA))

  const previewDate = useMemo(() => {
    if (timePreset === 'now') return new Date()
    if (timePreset === 'custom') return customDate
    const [h, m] = timePreset.split(':').map(Number)
    const d = new Date()
    d.setHours(h, m, 0, 0)
    return d
  }, [timePreset, customDate])

  function handleSelectPreset(preset) {
    setTimePreset(preset)
    setPreviewMode(null)
    setMaintenancePreview(false)
  }

  function handleCustomDateChange(e) {
    const val = e.target.value
    if (!val) return
    const next = new Date(val)
    if (!Number.isNaN(next.getTime())) {
      setCustomDate(next)
      setTimePreset('custom')
      setPreviewMode(null)
      setMaintenancePreview(false)
    }
  }

  const previewConditionLabel = useMemo(() => {
    if (maintenancePreview) return '강제 상태 · 홈 시스템 점검'
    if (previewMode) {
      const mode = ADMIN_HOME_HERO_PREVIEW_MODES.find(entry => entry.id === previewMode)
      return `강제 상태 · ${mode?.label ?? previewMode}`
    }
    return `자동 · ${formatSimulatedDate(previewDate)}`
  }, [maintenancePreview, previewDate, previewMode])

  useEffect(() => {
    function isEditableTarget(target) {
      if (!(target instanceof Element)) return false
      return Boolean(target.closest('input, textarea, select, [contenteditable="true"]'))
    }

    function handlePreviewKeydown(event) {
      if (workspaceMode !== 'preview' || qaSubMode !== 'all' || event.metaKey || event.ctrlKey || event.altKey || isEditableTarget(event.target)) return
      const stateDirection = event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0
      const timeDirection = event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0
      if (!stateDirection && !timeDirection) return

      event.preventDefault()
      if (stateDirection) {
        const currentId = maintenancePreview ? 'maintenance' : previewMode
        const currentIndex = HERO_QA_STATE_ORDER.findIndex(entry => entry.id === currentId)
        const nextIndex = currentIndex < 0
          ? (stateDirection > 0 ? 0 : HERO_QA_STATE_ORDER.length - 1)
          : (currentIndex + stateDirection + HERO_QA_STATE_ORDER.length) % HERO_QA_STATE_ORDER.length
        const nextState = HERO_QA_STATE_ORDER[nextIndex]
        if (nextState.id === 'maintenance') {
          setPreviewMode(null)
          setMaintenancePreview(true)
        } else {
          setMaintenancePreview(false)
          setPreviewMode(nextState.id)
        }
        return
      }

      const currentIndex = TIME_PRESETS.indexOf(timePreset)
      const nextIndex = (currentIndex + timeDirection + TIME_PRESETS.length) % TIME_PRESETS.length
      handleSelectPreset(TIME_PRESETS[nextIndex])
    }

    window.addEventListener('keydown', handlePreviewKeydown)
    return () => window.removeEventListener('keydown', handlePreviewKeydown)
  }, [maintenancePreview, previewMode, timePreset, workspaceMode])

  useEffect(() => {
    if (workspaceMode !== 'preview') return
    const selected = statePanelRef.current?.querySelector('.admin-home-state-selector__item.active')
    selected?.scrollIntoView?.({ block: 'nearest' })
  }, [previewMode, maintenancePreview, workspaceMode])

  async function load() {
    setLoading(true); setError('')
    try {
      const { production: prod, draft: dr } = await fetchCmsContent('homeConfig')
      setProduction(prod); setDraft(dr)
      const initial = dr?.data ?? prod?.data ?? EMPTY_HOME_CONFIG_DATA
      setConfig(initial)
      savedSnapshotRef.current = JSON.stringify(initial)
      setVersions(await fetchCmsVersions('homeConfig'))
    } catch (err) { setError(err.message) } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const dirty = JSON.stringify(config) !== savedSnapshotRef.current
  const validation = useMemo(() => validateHomeConfigDraft(config), [config])

  async function saveDraft() {
    setBusy(true); setMessage(''); setError('')
    try {
      await saveCmsDraft('homeConfig', config, { uid: auth.user.uid, baseVersion: production?.version ?? 0 })
      savedSnapshotRef.current = JSON.stringify(config)
      const { draft: fresh } = await fetchCmsContent('homeConfig')
      setDraft(fresh)
      setMessage('초안을 저장했습니다.')
    } catch (err) { setError(err.message) } finally { setBusy(false) }
  }

  async function discardDraft() {
    if (!window.confirm('저장된 초안을 버리고 배포된 내용으로 되돌릴까요?')) return
    setBusy(true); setMessage(''); setError('')
    try { await discardCmsDraft('homeConfig', auth.user.uid); await load(); setMessage('초안을 버렸습니다.') }
    catch (err) { setError(err.message) } finally { setBusy(false) }
  }

  async function publish() {
    if (!validation.valid) { setError('치명적 오류가 있어 반영할 수 없습니다.'); return }
    setBusy(true); setMessage(''); setError('')
    try {
      const version = await publishCmsContent('homeConfig', config, { uid: auth.user.uid })
      await load()
      setMessage(`v${version}으로 운영에 반영했습니다.`)
    } catch (err) { setError(err.validation ? '치명적 오류가 있어 반영할 수 없습니다.' : err.message) } finally { setBusy(false) }
  }

  async function rollback(version) {
    if (!window.confirm(`v${version}으로 롤백할까요? 현재 미저장 초안은 사라집니다.`)) return
    setBusy(true); setMessage(''); setError('')
    try { const next = await rollbackCmsContent('homeConfig', version, { uid: auth.user.uid }); await load(); setMessage(`v${version} → v${next}로 롤백했습니다.`) }
    catch (err) { setError(err.message) } finally { setBusy(false) }
  }

  function toggleDisabled(id) {
    setConfig(current => ({ ...current, disabledFeatures: current.disabledFeatures.includes(id) ? current.disabledFeatures.filter(x => x !== id) : [...current.disabledFeatures, id] }))
  }

  // 점검 메시지 단일 토글 — hero.mode를 함께 동기화해 저장한다(데이터 계약 유지). 화면 표시
  // 여부 판정은 shouldShowHomeMaintenanceBanner(enabled+문구) 하나로 관리자/학생 앱이 같다.
  function toggleMaintenanceMessage(enabled) {
    setConfig(current => ({ ...current, hero: { ...current.hero, mode: enabled ? 'maintenance' : 'auto' }, maintenanceMessage: { ...current.maintenanceMessage, enabled } }))
  }

  if (loading) return <div className="empty-state">홈 설정을 불러오는 중…</div>

  return (
    <div className="home-config-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">콘텐츠</p>
          <h1>홈 화면</h1>
          <p>학생 앱 홈의 노출과 운영 설정을 관리합니다.</p>
        </div>
        {canEdit && workspaceMode === 'edit' && (
          <div className="header-actions">
            {dirty && <span className="status-badge new">미저장 변경</span>}
            <button className="button secondary" type="button" disabled={busy || !dirty} onClick={saveDraft}><Save size={16} />저장</button>
            <button className="button primary" type="button" disabled={busy || !validation.valid} onClick={publish}><Send size={16} />운영 반영</button>
          </div>
        )}
      </header>

      {error && workspaceMode === 'edit' && <div className="error-state">{error}</div>}
      {message && <div className="temporary-warning">{message}</div>}

      {validation.errors.length > 0 && (
        <div className="error-state" role="alert" style={{ marginBottom: 16 }}>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {validation.errors.map((entry, idx) => <li key={idx}>{entry.field}: {entry.message}</li>)}
          </ul>
        </div>
      )}

      <div className="home-config-workspace-tabs" role="tablist" aria-label="홈 화면 작업 모드">
        <button
          type="button"
          role="tab"
          aria-selected={workspaceMode === 'preview' && qaSubMode === 'all'}
          className={workspaceMode === 'preview' && qaSubMode === 'all' ? 'active' : ''}
          onClick={() => { setWorkspaceMode('preview'); setQaSubMode('all') }}
        >
          전체 Hero QA
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={workspaceMode === 'preview' && qaSubMode === 'shuttle'}
          className={workspaceMode === 'preview' && qaSubMode === 'shuttle' ? 'active' : ''}
          onClick={() => { setWorkspaceMode('preview'); setQaSubMode('shuttle') }}
        >
          셔틀 Visual QA
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={workspaceMode === 'edit'}
          className={workspaceMode === 'edit' ? 'active' : ''}
          onClick={() => setWorkspaceMode('edit')}
        >
          홈 설정
        </button>
      </div>

      <div className={`home-config-layout home-config-layout--${workspaceMode}${workspaceMode === 'preview' && qaSubMode === 'shuttle' ? ' home-config-layout--shuttle-qa' : ''}`}>
        {workspaceMode === 'edit' && <section className="panel home-config-settings">
          <div className="form-section">
            <h2>점검 메시지</h2>
            <label className="check-chip" style={{ marginBottom: 12 }}><input type="checkbox" disabled={!canEdit} checked={config.maintenanceMessage.enabled} onChange={e => toggleMaintenanceMessage(e.target.checked)} />점검 메시지 사용</label>
            <textarea className="textarea" disabled={!canEdit || !config.maintenanceMessage.enabled} placeholder="예: 학사포털 점검으로 일부 정보가 지연될 수 있습니다." value={config.maintenanceMessage.message} onChange={e => setConfig(c => ({ ...c, maintenanceMessage: { ...c.maintenanceMessage, message: e.target.value } }))} />
            <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--ink-sub, #64748b)' }}>사용을 켜면 홈 히어로가 점검 배너로 전환됩니다. 학생 앱에는 운영 반영 후 홈 재진입·앱 재실행 시 반영돼요.</p>
          </div>
          <div className="form-section">
            <h2>오늘의 바로가기</h2>
            <CardOrderGroup group="todayQuickChips" labels={TODAY_CHIP_LABELS} config={config} setConfig={setConfig} canEdit={canEdit} />
          </div>
          <div className="form-section">
            <h2>기능 임시 숨김</h2>
            <div className="checkbox-group">
              {Object.entries(DISABLEABLE_LABELS).map(([id, label]) => (
                <label className="check-chip" key={id}><input type="checkbox" disabled={!canEdit} checked={config.disabledFeatures.includes(id)} onChange={() => toggleDisabled(id)} />{label}</label>
              ))}
            </div>
          </div>
          <details className="form-section" style={{ borderTop: '1px solid var(--line, #e2e8f0)', paddingTop: 12 }}>
            <summary style={{ cursor: canEdit ? 'pointer' : 'default', fontWeight: 700, fontSize: 15, marginBottom: 10 }}>상세 설정</summary>
            <div className="form-section" style={{ marginTop: 12 }}>
              <h3 style={{ margin: '0 0 10px', fontSize: 13 }}>기본 카드 노출</h3>
              <label className="check-chip" style={{ marginBottom: 10, display: 'flex' }}><input type="checkbox" disabled={!canEdit} checked={config.cards.classicMealMapRow.visible} onChange={e => setConfig(c => ({ ...c, cards: { ...c.cards, classicMealMapRow: { visible: e.target.checked } } }))} />학식 + 캠퍼스 지도 카드(클래식 홈)</label>
              <label className="check-chip" style={{ display: 'flex' }}><input type="checkbox" disabled={!canEdit} checked={config.cards.classicSugiInstantCard.visible} onChange={e => setConfig(c => ({ ...c, cards: { ...c.cards, classicSugiInstantCard: { visible: e.target.checked } } }))} />수기즉답 바로가기</label>
            </div>
            <div className="form-section">
              <h3 style={{ margin: '0 0 10px', fontSize: 13 }}>기본 유틸 메뉴(클래식 홈)</h3>
              <CardOrderGroup group="classicUtilityRow" labels={UTILITY_LABELS} config={config} setConfig={setConfig} canEdit={canEdit} />
            </div>
          </details>
          {canEdit && (draft || versions.length > 0) && (
            <div className="form-section home-config-version-row">
              {draft && <button className="button secondary" type="button" disabled={busy} onClick={discardDraft}><RotateCcw size={15} />초안 버리기</button>}
              {versions.length > 0 && (
                <select className="select" aria-label="이전 버전으로 롤백" disabled={busy} value="" onChange={e => e.target.value && rollback(Number(e.target.value))}>
                  <option value="">이전 버전으로 롤백…</option>
                  {versions.map(v => <option key={v.version} value={v.version}>v{v.version} · {formatDateTime(v.publishedAt)}</option>)}
                </select>
              )}
            </div>
          )}
        </section>}

        {workspaceMode === 'preview' && qaSubMode === 'shuttle' && (
          <div className="home-config-shuttle-qa-wrapper">
            <ShuttleVisualQa />
          </div>
        )}

        {workspaceMode === 'preview' && qaSubMode === 'all' && (
          <>
            <div className="home-config-preview">
              <div className="home-config-preview__heading">
                <div>
                  <p className="eyebrow">보기 / 상태 검증 전용</p>
                  <h2>홈 히어로 QA</h2>
                  <p className="home-config-preview__description">실제 사용자에게 노출되는 Hero 상태를 확인합니다.</p>
                </div>
                <div className="home-config-preview__meta">
                  <span className="home-config-preview__condition" aria-live="polite">{previewConditionLabel}</span>
                </div>
              </div>

              <div className="admin-time-simulator" role="region" aria-label="시간 시뮬레이터">
                <span className="admin-time-simulator__label">미리보기 기준 시각</span>
                <div className="admin-time-simulator__presets">
                  <button
                    type="button"
                    className={`admin-time-btn${timePreset === 'now' ? ' active' : ''}`}
                    onClick={() => handleSelectPreset('now')}
                  >
                    현재 시각
                  </button>
                  {['09:00', '12:00', '15:00', '18:00', '21:00'].map(t => (
                    <button
                      key={t}
                      type="button"
                      className={`admin-time-btn${timePreset === t ? ' active' : ''}`}
                      onClick={() => handleSelectPreset(t)}
                    >
                      {t}
                    </button>
                  ))}
                  <label className={`admin-time-btn admin-time-btn--custom${timePreset === 'custom' ? ' active' : ''}`}>
                    <span>직접 선택</span>
                    <input
                      type="datetime-local"
                      value={toInputDateTimeValue(previewDate)}
                      onChange={handleCustomDateChange}
                      aria-label="미리보기 날짜 및 시간 직접 선택"
                    />
                  </label>
                </div>
              </div>

              <div className="home-config-preview__stage">
                <HomeHeroPreview
                  config={config}
                  modeId={previewMode}
                  maintenancePreview={maintenancePreview}
                  previewDate={previewDate}
                />
              </div>
              <p className="home-config-preview__shortcut-hint" aria-label="키보드 단축키">↑ ↓ 상태 이동 <span>·</span> ← → 시간 이동</p>
            </div>

            <aside ref={statePanelRef} id="home-hero-state-panel" className="home-config-state-panel" aria-label="Hero 상태 패널">
              <HomeHeroStateSelector
                value={previewMode}
                onChange={setPreviewMode}
                maintenancePreview={maintenancePreview}
                onMaintenancePreviewChange={setMaintenancePreview}
              />
            </aside>
          </>
        )}
      </div>
    </div>
  )
}
